import {
  isFidelityMessageType,
  type CheckPublicFidelityCustomerResult,
  type FidelityCard,
  type FidelityConf,
  type FidelityCustomerSummary,
  type FidelityMessageType,
  type FidelityThreshold,
  type PageFidelityCard,
} from '../types/fidelity.types';
import { normalizeTagNames } from './fidelity-formatters';

function toNumber(value: unknown, fallback = 0): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function toString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function toBoolean(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === 'true' || value === 1 || value === '1') {
    return true;
  }

  if (value === 'false' || value === 0 || value === '0') {
    return false;
  }

  return fallback;
}

function normalizeMessageType(value: unknown): FidelityMessageType {
  return typeof value === 'string' && isFidelityMessageType(value)
    ? value
    : 'FIDELITY_1';
}

function normalizeThreshold(raw: unknown): FidelityThreshold | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;

  return {
    lowerBound: toNumber(item.lowerBound),
    ...(item.upperBound == null || item.upperBound === ''
      ? {}
      : { upperBound: toNumber(item.upperBound) }),
    points: toNumber(item.points),
    code: toString(item.code),
    hexColor: toString(item.hexColor),
  };
}

export function normalizeCustomerSummary(
  raw: unknown,
): FidelityCustomerSummary | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;

  return {
    customerId: toNumber(item.customerId) || undefined,
    customerCode: toString(item.customerCode) || undefined,
    firstName: toString(item.firstName) || undefined,
    lastName: toString(item.lastName) || undefined,
    email: toString(item.email) || undefined,
    phone: toString(item.phone) || undefined,
    prefix: toString(item.prefix) || undefined,
    tagsList: normalizeTagNames(item.tagsList),
    imageUrl: typeof item.imageUrl === 'string' ? item.imageUrl : null,
    isWalkInCustomer:
      typeof item.isWalkInCustomer === 'boolean'
        ? item.isWalkInCustomer
        : undefined,
  };
}

export function normalizeCheckPublicFidelityCustomer(
  raw: unknown,
): CheckPublicFidelityCustomerResult {
  if (!raw || typeof raw !== 'object') {
    return { available: false, customer: null };
  }

  const item = raw as Record<string, unknown>;

  return {
    available: toBoolean(item.available, false),
    customer: normalizeCustomerSummary(item.customer),
  };
}

export function normalizeFidelityConf(raw: unknown): FidelityConf | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  if (item.fidelityType === 'DISCOUNT') return null;
  const thresholdsRaw = Array.isArray(item.thresholds) ? item.thresholds : [];

  return {
    expiresOn: typeof item.expiresOn === 'string' ? item.expiresOn : null,
    pointsPolicy:
      item.pointsPolicy && typeof item.pointsPolicy === 'object'
        ? (item.pointsPolicy as FidelityConf['pointsPolicy'])
        : undefined,
    historicalThresholds: Array.isArray(item.historicalThresholds)
      ? item.historicalThresholds
          .map(normalizeThreshold)
          .filter(
            (threshold): threshold is FidelityThreshold => threshold !== null,
          )
      : undefined,
    milestones: Array.isArray(item.milestones)
      ? (item.milestones as Array<Record<string, unknown>>)
      : [],
    historicalTiers: Array.isArray(item.historicalTiers)
      ? (item.historicalTiers as FidelityConf['historicalTiers'])
      : [],
    historicalMilestones: Array.isArray(item.historicalMilestones)
      ? (item.historicalMilestones as Array<Record<string, unknown>>)
      : [],
    fidelityId: toNumber(item.fidelityId),
    branchCode: toString(item.branchCode),
    fidelityCode: toString(item.fidelityCode),
    fidelityName: toString(item.fidelityName),
    fidelityDescription: toString(item.fidelityDescription),
    tagsApplied: toString(item.tagsApplied),
    tagsAppliedOnDownload: toString(item.tagsAppliedOnDownload),
    fidelityType: 'POINTS',
    customerCount: toNumber(item.customerCount),
    thresholds: thresholdsRaw
      .map(normalizeThreshold)
      .filter(
        (threshold): threshold is FidelityThreshold => threshold !== null,
      ),
    createdAt: typeof item.createdAt === 'string' ? item.createdAt : null,
    active: toBoolean(item.active, false),
    hexColor: toString(item.hexColor),
    messageType: normalizeMessageType(item.messageType),
  };
}

export function normalizeFidelityConfList(raw: unknown): FidelityConf[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeFidelityConf)
      .filter((item): item is FidelityConf => item !== null);
  }

  if (raw && typeof raw === 'object' && 'content' in raw) {
    const pageData = (raw as { content?: unknown }).content;
    if (Array.isArray(pageData)) {
      return pageData
        .map(normalizeFidelityConf)
        .filter((item): item is FidelityConf => item !== null);
    }
  }

  const single = normalizeFidelityConf(raw);
  return single ? [single] : [];
}

export function normalizeFidelityCard(raw: unknown): FidelityCard | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }

  const item = raw as Record<string, unknown>;
  const operationsRaw = Array.isArray(item.fidelityOperationList)
    ? item.fidelityOperationList
    : [];

  return {
    fidelityCardId: toNumber(item.fidelityCardId),
    customerId: toNumber(item.customerId),
    fidelityCode: toString(item.fidelityCode),
    points: toNumber(item.points),
    historicalPoints: toNumber(item.historicalPoints),
    fidelityCardCode: toString(item.fidelityCardCode),
    activationDate:
      typeof item.activationDate === 'string' ? item.activationDate : null,
    deactivationDate:
      typeof item.deactivationDate === 'string' ? item.deactivationDate : null,
    prepaidAmount: toNumber(item.prepaidAmount),
    used: toBoolean(item.used),
    fidelityOperationList:
      operationsRaw as FidelityCard['fidelityOperationList'],
    fidelityCardQrUrl:
      typeof item.fidelityCardQrUrl === 'string'
        ? item.fidelityCardQrUrl
        : null,
    customerDTO: normalizeCustomerSummary(item.customerDTO ?? item.customer),
  };
}

export function normalizeFidelityCardList(raw: unknown): FidelityCard[] {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw
    .map(normalizeFidelityCard)
    .filter((item): item is FidelityCard => item !== null);
}

const EMPTY_PAGE: PageFidelityCard = {
  content: [],
  totalPages: 0,
  totalElements: 0,
  numberOfElements: 0,
  size: 0,
  number: 0,
  first: true,
  last: true,
  empty: true,
};

export function normalizeFidelityCardPage(raw: unknown): PageFidelityCard {
  if (!raw || typeof raw !== 'object') {
    return EMPTY_PAGE;
  }

  const item = raw as Record<string, unknown>;
  const content = normalizeFidelityCardList(item.content);

  return {
    content,
    totalPages: toNumber(item.totalPages),
    totalElements: toNumber(item.totalElements),
    numberOfElements: toNumber(item.numberOfElements),
    size: toNumber(item.size),
    number: toNumber(item.number),
    first: toBoolean(item.first, true),
    last: toBoolean(item.last, true),
    empty: toBoolean(item.empty, content.length === 0),
  };
}
